import { ImageOff } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { photoKindLabel } from "../reportPhoto";
import type { ToiletPhoto } from "../types";

const statusLabel: Record<ToiletPhoto["status"], string> = {
  approved: "掲載写真",
  pending: "掲載確認中",
  local: "この端末でのみ表示",
  failed: "未送信",
};

const photoAssetKey = (photoId: string, url: string) => `${photoId}\u0000${url}`;
const THUMBNAIL_SCROLL_SETTLE_MS = 160;

export function ToiletPhotoGallery({ photos }: { photos: ToiletPhoto[] }) {
  const [selectedId, setSelectedId] = useState(photos[0]?.id);
  const [failedFullAssets, setFailedFullAssets] = useState<Set<string>>(() => new Set());
  const [failedThumbnailAssets, setFailedThumbnailAssets] = useState<Set<string>>(
    () => new Set(),
  );
  const railRef = useRef<HTMLDivElement>(null);
  const scrollSettleTimerRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (!photos.some((photo) => photo.id === selectedId)) {
      setSelectedId(photos[0]?.id);
    }
  }, [photos, selectedId]);

  useEffect(
    () => () => {
      if (scrollSettleTimerRef.current !== undefined) {
        window.clearTimeout(scrollSettleTimerRef.current);
      }
    },
    [],
  );

  const selectedPhoto = useMemo(
    () => photos.find((photo) => photo.id === selectedId) ?? photos[0],
    [photos, selectedId],
  );

  if (!selectedPhoto) return null;

  const selectPhoto = (photo: ToiletPhoto, button: HTMLButtonElement) => {
    setSelectedId(photo.id);
    const reducedMotion =
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
    button.scrollIntoView?.({
      behavior: reducedMotion ? "auto" : "smooth",
      block: "nearest",
      inline: "center",
    });
  };

  const scheduleCenteredThumbnailSelection = () => {
    if (scrollSettleTimerRef.current !== undefined) {
      window.clearTimeout(scrollSettleTimerRef.current);
    }
    scrollSettleTimerRef.current = window.setTimeout(() => {
      scrollSettleTimerRef.current = undefined;
      const rail = railRef.current;
      if (!rail) return;
      const railCenter = rail.getBoundingClientRect().left + rail.clientWidth / 2;
      let closestId: string | undefined;
      let closestDistance = Number.POSITIVE_INFINITY;
      rail.querySelectorAll<HTMLButtonElement>("[data-photo-id]").forEach((button) => {
        const rect = button.getBoundingClientRect();
        const distance = Math.abs(rect.left + rect.width / 2 - railCenter);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestId = button.dataset.photoId;
        }
      });
      if (closestId) setSelectedId(closestId);
    }, THUMBNAIL_SCROLL_SETTLE_MS);
  };

  const selectedIndex = photos.findIndex((photo) => photo.id === selectedPhoto.id);
  const selectedAlt = `投稿写真 ${selectedIndex + 1}/${photos.length}：${photoKindLabel[selectedPhoto.kind]}`;
  const selectedFullAsset = photoAssetKey(selectedPhoto.id, selectedPhoto.fullUrl);

  return (
    <section className="toilet-photo-gallery" aria-labelledby="toilet-photo-title">
      <div className="photo-gallery-heading">
        <h3 id="toilet-photo-title">場所・外観の写真</h3>
        <span>{photos.length}枚</span>
      </div>

      <figure className="photo-gallery-stage">
        {failedFullAssets.has(selectedFullAsset) ? (
          <div className="photo-gallery-fallback">
            <div role="img" aria-label={`${selectedAlt}を読み込めませんでした`}>
              <ImageOff aria-hidden="true" />
              <span>写真を読み込めませんでした</span>
            </div>
            <button
              type="button"
              onClick={() =>
                setFailedFullAssets((current) => {
                  const next = new Set(current);
                  next.delete(selectedFullAsset);
                  return next;
                })
              }
            >
              再読み込み
            </button>
          </div>
        ) : (
          <img
            src={selectedPhoto.fullUrl}
            alt={selectedAlt}
            decoding="async"
            onError={() =>
              setFailedFullAssets((current) => new Set(current).add(selectedFullAsset))
            }
          />
        )}
        <figcaption>
          <b>{photoKindLabel[selectedPhoto.kind]}</b>
          <span>{statusLabel[selectedPhoto.status]}</span>
        </figcaption>
      </figure>

      {photos.length > 1 && (
        <div
          ref={railRef}
          className="photo-thumbnail-rail"
          role="group"
          aria-label="表示する写真を選択"
          onScroll={scheduleCenteredThumbnailSelection}
        >
          {photos.map((photo, index) => (
            <button
              key={photo.id}
              type="button"
              className={photo.id === selectedPhoto.id ? "is-selected" : ""}
              aria-pressed={photo.id === selectedPhoto.id}
              aria-label={`投稿写真 ${index + 1}/${photos.length}を表示：${photoKindLabel[photo.kind]}`}
              data-photo-id={photo.id}
              onClick={(event) => selectPhoto(photo, event.currentTarget)}
            >
              {failedThumbnailAssets.has(photoAssetKey(photo.id, photo.thumbnailUrl)) ? (
                <ImageOff aria-hidden="true" />
              ) : (
                <img
                  src={photo.thumbnailUrl}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  onError={() =>
                    setFailedThumbnailAssets((current) =>
                      new Set(current).add(photoAssetKey(photo.id, photo.thumbnailUrl)),
                    )
                  }
                />
              )}
            </button>
          ))}
        </div>
      )}
      {selectedPhoto.status !== "approved" && (
        <p
          className={`photo-moderation-note${selectedPhoto.status === "failed" ? " is-failed" : ""}`}
          role="status"
        >
          {selectedPhoto.status === "pending"
            ? "この写真は掲載確認中です。確認が終わるまでは、この端末の詳細画面だけに表示します。"
            : selectedPhoto.status === "failed"
              ? "この写真は送信されていません。ページを再読み込みすると表示から消えます。接続を確認して、必要ならもう一度投稿してください。"
              : "実証表示の写真です。公開データとしては保存していません。"}
        </p>
      )}
    </section>
  );
}
